// D37: Backup reminder banner — shown when reminder is enabled AND backup is overdue.
// Overdue = never backed up with data present, OR last backup older than 7 days.
// Session-only dismiss via useState (reappears after page reload if still overdue).

import { useState } from "react";
import type { LifeOsApi } from "../lib/life-os-contract";
import { exportLifeOsState } from "../lib/dataPortability";
import { todayIso } from "../lib/date";
import { getBackupSnoozedUntil, getLastBackupAt, isBackupReminderEnabled, setLastBackupAtNow, snoozeBackupReminder } from "../lib/app-prefs";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function hasAnyData(api: LifeOsApi): boolean {
  const s = api.state;
  return (
    s.tasks.length > 0 ||
    s.notes.length > 0 ||
    s.transactions.length > 0
  );
}

function isBackupOverdue(): boolean {
  const lastIso = getLastBackupAt();
  if (!lastIso) return true; // never backed up
  // new Date(garbage) yields NaN WITHOUT throwing — treat unparseable as overdue.
  const elapsed = Date.now() - new Date(lastIso).getTime();
  return !Number.isFinite(elapsed) || elapsed > SEVEN_DAYS_MS;
}

export function BackupReminder({ api }: { api: LifeOsApi }) {
  const [dismissed, setDismissed] = useState(false);

  // Re-evaluate on every render so the banner hides immediately after export.
  const shouldShow =
    !dismissed &&
    Date.now() >= getBackupSnoozedUntil() && // persisted snooze (survives reload)
    isBackupReminderEnabled() &&
    hasAnyData(api) &&
    isBackupOverdue();

  if (!shouldShow) return null;

  function handleDownload() {
    const json = exportLifeOsState(api.state);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `life-os-${todayIso()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setLastBackupAtNow();
    setDismissed(true);
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="mb-3 flex items-center gap-2.5 rounded-xl bg-amber-50 px-3 py-2 text-[12px] font-medium text-amber-800 dark:bg-amber-500/15 dark:text-amber-200"
    >
      <span className="min-w-0 flex-1 truncate">💾 Đã hơn 7 ngày chưa sao lưu dữ liệu.</span>
      <button
        type="button"
        onClick={handleDownload}
        className="shrink-0 rounded-lg px-2.5 py-1 text-[12px] font-bold text-amber-900 transition hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-500/20"
      >
        Tải bản sao lưu
      </button>
      <button
        type="button"
        onClick={() => { snoozeBackupReminder(3); setDismissed(true); }}
        aria-label="Để sau 3 ngày"
        title="Ẩn nhắc sao lưu trong 3 ngày"
        className="grid h-6 w-6 shrink-0 place-items-center rounded-lg text-amber-700 transition hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-500/20"
      >
        ✕
      </button>
    </div>
  );
}
